'use client';

import { apiFetch } from '@/lib/utils/api';
import { ProfileModal as ProfileModalUi } from '@slims/ui/organisms';
import { useRouter } from '@/lib/next/navigation';
import { useState } from 'react';
import { toast } from 'sonner';
import { createSupabaseBrowser } from '@/lib/supabase/client';
import { useContextOrg } from '@/hooks/use-context-org';
import { useAuthStore } from '@/stores/auth-store';
import { useCERStore } from '@/stores/cer-store';
import { useUIStore } from '@/stores/ui-store';
import { hasRoleAtLeast } from '@slims/shared/auth';

const ROLE_LABELS: Record<string, string> = {
	superadmin: 'Super Admin',
	admin: 'Amministratore',
	referent: 'Referente',
	member: 'Socio',
};

export function ProfileModal() {
	const router = useRouter();
	const activeModal = useUIStore((s) => s.activeModal);
	const closeModal = useUIStore((s) => s.closeModal);
	const {
		email,
		role,
		fullName,
		avatarUrl,
		createdAt,
		setFullName,
		setAvatarUrl,
		memberTenantIds,
		classificaConsenso,
		setClassificaConsenso,
	} = useAuthStore();
	const [savingConsent, setSavingConsent] = useState(false);
	const contextOrg = useContextOrg();
	const config = useCERStore((s) => s.config);

	const roleLabel = ROLE_LABELS[role ?? ''] ?? role ?? '-';
	const cerName = contextOrg?.cerNome ?? config.nome ?? '-';
	// «Soci gestiti» è un dato di chi amministra: al socio non dice nulla (INB-377).
	const managesMembers = role === 'admin' || role === 'superadmin' || role === 'referent';
	const membersCount = managesMembers ? (config.membersCount ?? config.soci) : null;

	const handleSaveName = async (name: string) => {
		const res = await apiFetch('/api/auth/me', {
			method: 'PATCH',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ fullName: name }),
		});
		if (!res.ok) throw new Error('Errore nel salvataggio');
		setFullName(name || null);
	};

	const handleUploadAvatar = async (file: File) => {
		const fd = new FormData();
		fd.append('file', file);
		const res = await apiFetch('/api/auth/avatar', { method: 'POST', body: fd });
		if (!res.ok) {
			const err = (await res.json().catch(() => null)) as { error?: string } | null;
			throw new Error(err?.error ?? 'Upload fallito');
		}
		const { url } = (await res.json()) as { url: string };
		setAvatarUrl(url);
	};

	const handleDeleteAvatar = async () => {
		await apiFetch('/api/auth/avatar', { method: 'DELETE' });
		setAvatarUrl(null);
	};

	// Consenso al nome nelle classifiche (INB-357): solo per chi è socio.
	const rankingConsent = memberTenantIds.length
		? {
				checked: classificaConsenso,
				saving: savingConsent,
				onChange: async (value: boolean) => {
					setSavingConsent(true);
					try {
						const res = await apiFetch('/api/auth/me', {
							method: 'PATCH',
							headers: { 'Content-Type': 'application/json' },
							body: JSON.stringify({ classificaConsenso: value }),
						});
						if (!res.ok) throw new Error();
						setClassificaConsenso(value);
						toast.success(
							value
								? 'Il tuo nome comparirà nelle classifiche'
								: 'Nelle classifiche comparirai come socio anonimo',
						);
					} catch {
						toast.error('Errore nel salvataggio del consenso');
					} finally {
						setSavingConsent(false);
					}
				},
			}
		: null;

	const handleSignOut = async () => {
		const supabase = createSupabaseBrowser();
		await supabase.auth.signOut();
		closeModal();
		router.push('/sign-in');
	};

	return (
		<ProfileModalUi
			open={activeModal === 'profile'}
			rankingConsent={rankingConsent}
			user={{
				fullName,
				email,
				avatarUrl,
				roleLabel,
				cerName,
				membersCount,
				createdAt,
			}}
			onClose={closeModal}
			onSaveName={handleSaveName}
			onUploadAvatar={handleUploadAvatar}
			onDeleteAvatar={handleDeleteAvatar}
			// Le preferenze di notifica della configurazione stanno nella pagina
			// Profilo, aperta solo a chi amministra.
			onOpenPreferences={
				role && hasRoleAtLeast(role, 'admin')
					? () => {
							closeModal();
							router.push('/profilo');
						}
					: undefined
			}
			onSignOut={() => {
				handleSignOut().catch(() => {
					toast.error('Errore durante il logout');
				});
			}}
		/>
	);
}
